// Admin Stream Keys — '/admin/streams'.
//
// Ports the legacy `StreamKeys`, `StreamSettings`, `StreamPreview` and
// `SnippetGeneratorModal` React components from
// www/livevane.com/htdocs/admin/index.html into a TanStack file-based
// route under the /admin layout.
//
// Auth gate:
//   NOT re-added in this file. The parent layout at
//   apps/web/src/routes/admin/route.tsx already throws-redirects to
//   /login when `token === null || user?.role !== 'admin'`.
//
// API endpoints (verified against legacy admin + backing PHP):
//   - GET    /api/streams/                       → active + history sessions
//     (www/livevane.com/api/streams/index.php). We normalize defensively
//     against the three shapes the legacy code can encounter and fall
//     back to the /stream/ detail query for the canonical per-key data.
//   - GET    /api/stream/${vaSuffix}             → per-admin StreamSummary[]
//     (www/livevane.com/api/stream/index.php) — the "per-key detail" call.
//   - GET    /api/bitrate/${vaSuffix}            → token→{calc_kbps,probe,...}
//     (www/livevane.com/api/bitrate/index.php). The legacy warning
//     (`Input ${kbps}k exceeds encoder maxrate cap (6000k)`) is preserved
//     verbatim by `computeHealthFlags`.
//   - GET    /api/system/settings/embed_origins/${vaSuffix} → embed allowlist
//     used to suggest the default origin in the embed snippet generator.
//
// Vocabulary: the object on this screen is a "stream key" everywhere —
// in buttons, dialogs and copy. "Token" is the API field name only.

import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, Pencil } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { FlowStatePill } from '@/components/flows/FlowStatePill'
import { getFlowState } from '@/components/flows/flow-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { InlineError } from '@/components/ui'
import {
  apiClient,
  scopedApiPath,
  toFormDataPhp,
} from '@/lib/api-client'
import { describeError } from '@/lib/errors'
import {
  countLabel,
  formatBitrate,
  formatBitrateParts,
  formatDateTime,
  formatDuration,
} from '@/lib/format'
import { cn } from '@/lib/utils'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  BitrateSettings,
  BitrateStream,
  EmbedOriginsSettings,
  FlowListItem,
  FlowsListResponse,
  StreamDetailResponse,
  StreamSummary,
  SystemSettingMutationResponse,
} from '@/lib/types'

export const Route = createFileRoute('/admin/streams')({
  component: AdminStreamsPage,
})

// ── react-query keys ────────────────────────────────────────────────
const QK_STREAMS_LIST = ['admin', 'streams', 'list'] as const
const QK_STREAM_DETAIL = (suffix: string) =>
  ['admin', 'streams', 'detail', suffix] as const
const QK_BITRATE = (suffix: string) =>
  ['admin', 'streams', 'bitrate', suffix] as const
const QK_EMBED_ORIGINS = ['admin', 'streams', 'embed-origins'] as const

// Legacy line 1610: warn when input bitrate exceeds the encoder maxrate
// cap. The bucket is `> 7500 kbps` for the warning ("(6000k)" is the cap
// label, not the threshold).
const ENCODER_MAXRATE_LABEL_KBPS = 6000
const ENCODER_INPUT_WARN_KBPS = 7500
const ENCODER_INPUT_ERROR_KBPS = 1500

type StreamsListShape =
  | StreamSummary[]
  | { streams?: StreamSummary[] }
  | { active?: unknown; history?: unknown }

type RegenerateTokenResponse = SystemSettingMutationResponse & {
  token?: string
}

/**
 * Three legacy shapes are possible from /api/streams/:
 *  1. bare `StreamSummary[]` (older deployments)
 *  2. `{ streams: StreamSummary[] }` envelope (matches /api/stream/)
 *  3. `{ success, active, history }` session envelope (current PHP). The
 *     session rows do NOT contain `token`/`name`/`stream_url`, so we
 *     always fall back to the /api/stream/ detail call. Returning `[]`
 *     here keeps the listQuery cheap and lets the UI render from
 *     `detailQuery` instead.
 */
function normalizeStreamsList(
  payload: StreamsListShape | undefined | null,
): StreamSummary[] {
  if (Array.isArray(payload)) return payload
  if (
    payload &&
    typeof payload === 'object' &&
    'streams' in payload &&
    Array.isArray((payload as { streams?: unknown }).streams)
  ) {
    return (payload as { streams: StreamSummary[] }).streams
  }
  return []
}

function maskToken(token: string): string {
  if (!token) return ''
  return '•'.repeat(Math.min(token.length, 24))
}

function copyText(text: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    void navigator.clipboard.writeText(text)
  }
}

/**
 * Mirror of `computeHealthFlags` at legacy line 1600. The exact
 * thresholds and message strings are preserved so support docs that
 * reference the legacy warnings keep matching what users see.
 */
function computeHealthFlags(
  br: BitrateStream | undefined,
): ReadonlyArray<{ level: 'warn' | 'error'; msg: string }> {
  if (!br) return []
  const flags: Array<{ level: 'warn' | 'error'; msg: string }> = []
  const kbps = br.calc_kbps ?? br.bw_in_kbps ?? 0
  if (kbps > 0) {
    if (kbps < ENCODER_INPUT_ERROR_KBPS) {
      flags.push({
        level: 'error',
        msg: `Input bitrate very low (${kbps}k) — poor encode quality`,
      })
    } else if (kbps > ENCODER_INPUT_WARN_KBPS) {
      flags.push({
        level: 'warn',
        msg: `Input ${kbps}k exceeds encoder maxrate cap (${ENCODER_MAXRATE_LABEL_KBPS}k)`,
      })
    }
  }
  if (br.video_codec) {
    const vc = br.video_codec.toLowerCase()
    if (!['h264', 'avc', 'avc1'].includes(vc)) {
      flags.push({
        level: 'warn',
        msg: `Non-H264 video (${br.video_codec}) — extra CPU overhead`,
      })
    }
  }
  if (br.audio_codec) {
    const ac = br.audio_codec.toLowerCase()
    if (!['aac', 'mp4a', 'mp4a-latm'].includes(ac)) {
      flags.push({
        level: 'warn',
        msg: `Non-AAC audio (${br.audio_codec}) — will be re-encoded`,
      })
    }
  }
  if (br.audio_rate && br.audio_rate !== 48000 && br.audio_rate !== 44100) {
    flags.push({
      level: 'warn',
      msg: `Audio ${(br.audio_rate / 1000).toFixed(1)}kHz — output is 48kHz`,
    })
  }
  return flags
}

function AdminStreamsPage() {
  const queryClient = useQueryClient()
  // View-as impersonation is owned by the admin layout header dropdown.
  // Reading from the shared store re-keys every query on this page.
  const viewAsId = useViewAsStore((s) => s.viewAsId)
  const buildPath = (base: string): string => scopedApiPath(base, viewAsId)
  const vaSuffix: string = viewAsId == null ? '' : `view_as=${String(viewAsId)}`

  // ── Queries ──────────────────────────────────────────────────────
  // Poll cadence mirrors the pre-SPA admin (8s fetchAll loop).
  const LIVE_POLL_MS = 8000

  const listQuery = useQuery({
    queryKey: QK_STREAMS_LIST,
    queryFn: () => apiClient.get<StreamsListShape>('/streams/'),
    select: normalizeStreamsList,
    refetchInterval: LIVE_POLL_MS,
  })

  const detailQuery = useQuery({
    queryKey: QK_STREAM_DETAIL(vaSuffix),
    queryFn: () =>
      apiClient.get<StreamDetailResponse>(buildPath('/stream/')),
    refetchInterval: LIVE_POLL_MS,
  })

  const bitrateQuery = useQuery({
    queryKey: QK_BITRATE(vaSuffix),
    queryFn: () => apiClient.get<BitrateSettings>(buildPath('/bitrate/')),
    refetchInterval: LIVE_POLL_MS,
  })

  const embedOriginsQuery = useQuery({
    queryKey: QK_EMBED_ORIGINS,
    queryFn: () =>
      apiClient.get<EmbedOriginsSettings>('/system/settings/embed_origins/'),
  })

  // Shared query key ['flows'] mirrors flows.tsx so the cache is shared.
  const flowsQuery = useQuery({
    queryKey: ['flows'],
    queryFn: () => apiClient.get<FlowsListResponse>('/flows/'),
    refetchInterval: 5000,
  })
  const activeFlows: FlowListItem[] = (flowsQuery.data?.flows ?? []).filter(
    (f) => f.ACTIVE === 1,
  )

  // ── Local UI state ───────────────────────────────────────────────
  const [revealedKeys, setRevealedKeys] = useState<Record<number, boolean>>({})
  const [previewToken, setPreviewToken] = useState<string | null>(null)
  const [snippetToken, setSnippetToken] = useState<string | null>(null)
  const [setLiveError, setSetLiveError] = useState<string | null>(null)
  // Switching the live key changes what the public player shows, so it is
  // confirmed with the consequence named.
  const [pendingLiveStream, setPendingLiveStream] = useState<StreamSummary | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [regenerateError, setRegenerateError] = useState<string | null>(null)
  const [portalError, setPortalError] = useState<string | null>(null)
  const [editingStreamId, setEditingStreamId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState<string>('')
  const [editingPortalId, setEditingPortalId] = useState<number | null>(null)
  const [portalValue, setPortalValue] = useState<string>('')
  // A regenerated key stays on screen, with a Copy button, until the
  // operator hides it. No timer: a key that vanishes mid-copy is worse
  // than one that stays visible on an operator's own screen.
  const [regeneratedTokens, setRegeneratedTokens] = useState<Record<number, string>>({})
  const [pendingDelete, setPendingDelete] = useState<StreamSummary | null>(null)
  const [pendingRegenerate, setPendingRegenerate] = useState<StreamSummary | null>(null)
  // Where to return focus after a dialog opened from the row menu closes.
  const menuTriggerRef = useRef<HTMLElement | null>(null)
  // Add-key modal: the backend mints the RTMP key itself, so the operator
  // only supplies a name.
  const [createOpen, setCreateOpen] = useState<boolean>(false)
  const [createLabel, setCreateLabel] = useState<string>('')
  const [createError, setCreateError] = useState<string | null>(null)

  const toggleRevealKey = (id: number): void =>
    setRevealedKeys((prev) => ({ ...prev, [id]: !prev[id] }))

  const dismissRegenerated = (id: number): void => {
    setRegeneratedTokens((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setRevealedKeys((prev) => ({ ...prev, [id]: false }))
  }

  const startRenaming = (stream: StreamSummary): void => {
    setRenameError(null)
    setEditingStreamId(stream.id)
    setRenameValue(stream.name)
  }

  const cancelRenaming = (): void => {
    if (renameMutation.isPending) return
    setEditingStreamId(null)
    setRenameValue('')
    setRenameError(null)
  }

  const invalidateKeys = (): Promise<unknown> =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: QK_STREAMS_LIST }),
      queryClient.invalidateQueries({ queryKey: QK_STREAM_DETAIL(vaSuffix) }),
    ])

  const setLiveMutation = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/system/settings/live/'),
        toFormDataPhp({ id }),
      ),
    onSuccess: async () => {
      setSetLiveError(null)
      setPendingLiveStream(null)
      await Promise.all([
        invalidateKeys(),
        queryClient.invalidateQueries({ queryKey: ['admin', 'system-settings'] }),
      ])
    },
    onError: (err) => setSetLiveError(describeError(err)),
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/user/'),
        toFormDataPhp({ action: 'rename', id, label }),
      ),
    onSuccess: async () => {
      setRenameError(null)
      setEditingStreamId(null)
      setRenameValue('')
      await invalidateKeys()
    },
    onError: (err) => setRenameError(describeError(err)),
  })

  const regenerateTokenMutation = useMutation({
    mutationFn: ({ userId }: { streamId: number; userId: number }) =>
      apiClient.post<RegenerateTokenResponse>(
        buildPath('/user/'),
        toFormDataPhp({ action: 'regenerate', id: userId }),
      ),
    onSuccess: async (data, variables) => {
      setRegenerateError(null)
      setPendingRegenerate(null)
      if (data.token) {
        const token = data.token
        setRegeneratedTokens((prev) => ({ ...prev, [variables.streamId]: token }))
        setRevealedKeys((prev) => ({ ...prev, [variables.streamId]: true }))
      }
      await invalidateKeys()
    },
    onError: (err) => setRegenerateError(describeError(err)),
  })

  const updatePortalMutation = useMutation({
    mutationFn: ({ id, watchKey }: { id: number; watchKey: string }) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/user/'),
        toFormDataPhp({ action: 'update_portal', id, watch_key: watchKey }),
      ),
    onSuccess: async () => {
      setPortalError(null)
      setEditingPortalId(null)
      setPortalValue('')
      await invalidateKeys()
    },
    onError: (err) => setPortalError(describeError(err)),
  })

  // Delete a stream key. The backend routes this through POST /api/user/
  // with `action=delete` and the key's user id — there is no HTTP DELETE
  // verb here — and returns the refreshed key list.
  const deleteKeyMutation = useMutation({
    mutationFn: (id: number) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/user/'),
        toFormDataPhp({ action: 'delete', id }),
      ),
    onSuccess: async () => {
      setDeleteError(null)
      setPendingDelete(null)
      await invalidateKeys()
    },
    onError: (err) => setDeleteError(describeError(err)),
  })

  // Create a stream key. POST /api/user/ with `action=create` and a label;
  // the backend generates the key and returns the refreshed list.
  const createKeyMutation = useMutation({
    mutationFn: (label: string) =>
      apiClient.post<SystemSettingMutationResponse>(
        buildPath('/user/'),
        toFormDataPhp({ action: 'create', label }),
      ),
    onSuccess: async () => {
      setCreateError(null)
      setCreateOpen(false)
      setCreateLabel('')
      await invalidateKeys()
    },
    onError: (err) => setCreateError(describeError(err)),
  })

  // ── Render data ──────────────────────────────────────────────────
  const detailStreams: StreamSummary[] = detailQuery.data?.streams ?? []
  const listStreams: StreamSummary[] = listQuery.data ?? []
  const streams: StreamSummary[] =
    detailStreams.length > 0 ? detailStreams : listStreams
  const sortedStreams = useMemo<StreamSummary[]>(() => {
    // Sort by liveness first so the busy keys float to the top.
    const score = (s: StreamSummary): number => {
      if (s.rtmp_connected) return 2
      if (s.is_live) return 1
      return 0
    }
    return [...streams].sort((a, b) => score(b) - score(a))
  }, [streams])

  const totalConnected = streams.filter((s) => s.rtmp_connected).length
  const isLoading =
    listQuery.isLoading || detailQuery.isLoading || bitrateQuery.isLoading
  const rtmpServer = `rtmp://${
    typeof window !== 'undefined' ? window.location.hostname : 'your-host'
  }/stream`
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  const previewStream = useMemo<StreamSummary | null>(() => {
    if (!previewToken) return null
    return streams.find((s) => s.token === previewToken) ?? null
  }, [previewToken, streams])

  return (
    <div data-testid="admin-streams" className="space-y-4">
      <SectionCard
        title="Stream keys"
        description="Use any stream key below as the Stream Key in OBS or your encoder. Keys are private; share the viewing portal with your audience instead."
      >
        <div className="space-y-4">
          {/* Server line + the one primary action. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <p className="min-w-0 text-sm text-muted-foreground">
              <span>Server </span>
              <code className="font-mono text-[13px] text-foreground [overflow-wrap:anywhere]">{rtmpServer}</code>
              <span className="whitespace-nowrap">
                {' · '}
                {totalConnected > 0
                  ? `${countLabel(totalConnected, 'encoder')} connected`
                  : 'no encoders connected'}
                {detailQuery.data?.encoding === false ? ' · encoding off' : ''}
              </span>
            </p>
            <Button
              type="button"
              size="sm"
              data-testid="admin-streams-add-open"
              onClick={() => {
                setCreateError(null)
                setCreateLabel('')
                setCreateOpen(true)
              }}
            >
              Add key
            </Button>
          </div>

          {/* Only surface the list error when nothing is on screen. */}
          {listQuery.isError && streams.length === 0 && (
            <InlineError message={`Couldn't load stream keys: ${describeError(listQuery.error)}`} />
          )}
          {detailQuery.isError && (
            <InlineError message={`Couldn't load per-key details: ${describeError(detailQuery.error)}`} />
          )}
          {renameError !== null && (
            <InlineError message={`Couldn't rename the stream key: ${renameError}`} data-testid="admin-streams-rename-error" />
          )}
          {regenerateError !== null && pendingRegenerate === null && (
            <InlineError message={`Couldn't regenerate the stream key: ${regenerateError}`} data-testid="admin-streams-regenerate-error" />
          )}
          {portalError !== null && (
            <InlineError message={`Couldn't update the viewing portal address: ${portalError}`} data-testid="admin-streams-portal-error" />
          )}
          {deleteError !== null && pendingDelete === null && (
            <InlineError message={`Couldn't delete the stream key: ${deleteError}`} data-testid="admin-streams-delete-error" />
          )}
          {setLiveError !== null && pendingLiveStream === null && (
            <InlineError message={setLiveError} data-testid="admin-streams-set-live-error" />
          )}

          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-28 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          ) : sortedStreams.length === 0 ? (
            <div
              data-testid="admin-streams-empty"
              className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground"
            >
              No stream keys yet. Add a key to start publishing from OBS.
            </div>
          ) : (
            <ul className="space-y-3">
              {sortedStreams.map((stream) => {
                const br = bitrateQuery.data?.streams?.[stream.token]
                const flags = computeHealthFlags(br)
                const revealed = revealedKeys[stream.id] === true
                const regeneratedToken = regeneratedTokens[stream.id]
                const displayedToken = regeneratedToken ?? stream.token
                const isConnected = stream.rtmp_connected
                const isEditingThisStream = editingStreamId === stream.id
                const isEditingPortal = editingPortalId === stream.id
                const portalPath = `/live/${stream.watch_key}`
                const isRenamingThisStream =
                  renameMutation.isPending &&
                  renameMutation.variables?.id === stream.user_id
                const uptimeSeconds =
                  br?.uptime_ms != null ? Math.floor(br.uptime_ms / 1000) : stream.uptime_seconds
                const inKbps = br?.calc_kbps ?? br?.bw_in_kbps ?? null
                const outKbps = br?.probe?.effective_bitrate ?? br?.probe?.channel_cap ?? null
                const keyRevealId = `admin-streams-key-${stream.id}`
                return (
                  <li
                    key={stream.id}
                    data-testid="admin-streams-row"
                    data-stream-id={stream.id}
                    data-stream-token={stream.token}
                    className="rounded-lg border border-border/60 bg-background/40"
                  >
                    {/* Row header: name · state · active-key badge · actions */}
                    <div className="flex flex-col gap-3 border-b border-border/50 px-4 py-3 sm:flex-row sm:items-center">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {isEditingThisStream ? (
                          <form
                            className="flex min-w-0 flex-1 items-center gap-2"
                            onSubmit={(e) => {
                              e.preventDefault()
                              const trimmed = renameValue.trim()
                              if (
                                trimmed === '' ||
                                trimmed === stream.name ||
                                isRenamingThisStream
                              ) {
                                return
                              }
                              renameMutation.mutate({ id: stream.user_id, label: trimmed })
                            }}
                          >
                            <Input
                              autoFocus
                              aria-label="Stream key name"
                              data-testid="admin-streams-rename-input"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  e.preventDefault()
                                  cancelRenaming()
                                }
                              }}
                              className="h-8 min-w-0 flex-1"
                            />
                            <Button
                              type="submit"
                              size="sm"
                              className="h-8 shrink-0"
                              data-testid="admin-streams-rename-save"
                              disabled={
                                isRenamingThisStream ||
                                renameValue.trim() === '' ||
                                renameValue.trim() === stream.name
                              }
                            >
                              {isRenamingThisStream ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8 shrink-0"
                              data-testid="admin-streams-rename-cancel"
                              disabled={isRenamingThisStream}
                              onClick={cancelRenaming}
                            >
                              Cancel
                            </Button>
                          </form>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="flex min-w-0 items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              data-testid="admin-streams-rename-open"
                              aria-label={`Rename ${stream.name}`}
                              title="Rename"
                              onClick={() => startRenaming(stream)}
                            >
                              <h3
                                className="min-w-0 truncate text-base font-semibold text-foreground"
                                title={stream.name}
                              >
                                {stream.name}
                              </h3>
                              <Pencil className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                            </button>
                            <KeyStatePill live={isConnected} />
                            {stream.is_live ? (
                              <span
                                data-testid="admin-streams-active-key"
                                title="This key feeds the public player"
                                className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-cyber-cyan/40 bg-cyber-cyan/10 px-2 py-0.5 text-xs font-medium text-cyber-cyan"
                              >
                                Active key
                              </span>
                            ) : null}
                          </>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {!stream.is_live ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="admin-streams-set-live"
                            disabled={setLiveMutation.isPending}
                            aria-label={`Make ${stream.name} the active key`}
                            onClick={() => {
                              setSetLiveError(null)
                              setPendingLiveStream(stream)
                            }}
                          >
                            Set active
                          </Button>
                        ) : null}
                        {isConnected && stream.stream_url ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid="admin-streams-preview-open"
                            onClick={() => setPreviewToken(stream.token)}
                          >
                            Preview
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          data-testid="admin-streams-snippet-open"
                          onClick={() => setSnippetToken(stream.token)}
                        >
                          Embed
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="px-2"
                              aria-label={`More actions for ${stream.name}`}
                              onPointerDown={(e) => {
                                menuTriggerRef.current = e.currentTarget
                              }}
                              onKeyDown={(e) => {
                                menuTriggerRef.current = e.currentTarget
                              }}
                            >
                              <MoreHorizontal aria-hidden="true" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              data-testid="admin-streams-regenerate"
                              disabled={regenerateTokenMutation.isPending}
                              onSelect={() => {
                                setRegenerateError(null)
                                setPendingRegenerate(stream)
                              }}
                            >
                              Regenerate key
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid="admin-streams-delete"
                              className="text-destructive focus:text-destructive"
                              disabled={deleteKeyMutation.isPending}
                              onSelect={() => {
                                setDeleteError(null)
                                setPendingDelete(stream)
                              }}
                            >
                              Delete key
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    <div className="divide-y divide-border/40 px-4">
                      {/* Stream key — masked until revealed; copy-able. */}
                      <div
                        className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center"
                        data-testid="admin-streams-key"
                      >
                        <span className="w-32 shrink-0 text-xs text-muted-foreground">Stream key</span>
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <code
                              id={keyRevealId}
                              className={cn(
                                'min-w-0 flex-1 truncate font-mono text-[13px]',
                                revealed ? 'text-foreground' : 'text-muted-foreground',
                              )}
                              aria-label={revealed ? 'Stream key' : 'Stream key, hidden'}
                            >
                              {revealed ? displayedToken : maskToken(displayedToken)}
                            </code>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              aria-pressed={revealed}
                              aria-controls={keyRevealId}
                              onClick={() => toggleRevealKey(stream.id)}
                            >
                              {revealed ? 'Hide key' : 'Reveal key'}
                            </Button>
                            {revealed ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => copyText(displayedToken)}
                              >
                                Copy
                              </Button>
                            ) : null}
                          </div>
                          {regeneratedToken ? (
                            <div
                              role="status"
                              data-testid="admin-streams-regenerated-token"
                              className="flex flex-wrap items-center gap-2 rounded-md border border-cyber-cyan/40 bg-cyber-cyan/10 px-3 py-2 text-xs text-foreground"
                            >
                              <span className="min-w-0 flex-1">
                                New key issued. The old key has stopped working — paste this one into your encoder.
                              </span>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7"
                                onClick={() => copyText(regeneratedToken)}
                              >
                                Copy
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7"
                                onClick={() => dismissRegenerated(stream.id)}
                              >
                                Hide
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Public portal — stable per key, independent of the ingest key. */}
                      <div className="py-3" data-testid="admin-streams-portal">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <span className="w-32 shrink-0 text-xs text-muted-foreground">Viewing portal</span>
                          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                            <code className="min-w-0 basis-full truncate font-mono text-[13px] text-foreground sm:flex-1 sm:basis-0" title={`${origin}${portalPath}`}>
                              {origin}{portalPath}
                            </code>
                            <Button asChild type="button" variant="ghost" size="sm" className="h-8">
                              <a href={portalPath} target="_blank" rel="noopener noreferrer">Open</a>
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              onClick={() => copyText(`${origin}${portalPath}`)}
                            >
                              Copy
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              aria-expanded={isEditingPortal}
                              onClick={() => {
                                setPortalError(null)
                                setEditingPortalId(isEditingPortal ? null : stream.id)
                                setPortalValue(stream.watch_key)
                              }}
                            >
                              {isEditingPortal ? 'Cancel' : 'Edit address'}
                            </Button>
                          </div>
                        </div>
                        {isEditingPortal ? (
                          <form
                            className="mt-2 flex flex-col gap-2 sm:ml-32 sm:flex-row"
                            onSubmit={(event) => {
                              event.preventDefault()
                              const value = portalValue.trim().toLowerCase()
                              if (value !== '' && value !== stream.watch_key) {
                                updatePortalMutation.mutate({ id: stream.user_id, watchKey: value })
                              }
                            }}
                          >
                            <div className="flex min-w-0 flex-1 items-center rounded-md border border-input bg-background-elevated pl-3">
                              <span className="font-mono text-xs text-muted-foreground">/live/</span>
                              <Input
                                autoFocus
                                value={portalValue}
                                aria-label="Viewing portal address"
                                data-testid="admin-streams-portal-input"
                                className="h-9 border-0 bg-transparent font-mono shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                                onChange={(event) => setPortalValue(event.target.value.toLowerCase())}
                              />
                            </div>
                            <Button
                              type="submit"
                              size="sm"
                              disabled={
                                updatePortalMutation.isPending ||
                                portalValue.trim() === '' ||
                                portalValue.trim() === stream.watch_key
                              }
                            >
                              {updatePortalMutation.isPending ? 'Saving…' : 'Save address'}
                            </Button>
                          </form>
                        ) : null}
                      </div>

                      {/* Bitrate — read-only stats while a signal is arriving. */}
                      {isConnected ? (
                        <div className="py-3" data-testid="admin-streams-bitrate">
                          <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-4">
                            <BitrateStat label="Input" parts={formatBitrateParts(inKbps)} />
                            <BitrateStat label="Output" parts={formatBitrateParts(outKbps)} />
                            <BitrateStat
                              label="Resolution"
                              parts={{
                                value: br?.width && br?.height ? `${br.width}×${br.height}` : '—',
                                unit: br?.fps ? `${br.fps} fps` : '',
                              }}
                            />
                            <BitrateStat label="Uptime" parts={{ value: formatDuration(uptimeSeconds), unit: '' }} />
                          </dl>
                          {flags.length > 0 ? (
                            <ul className="mt-2 space-y-1">
                              {flags.map((f, i) => (
                                <li
                                  key={i}
                                  className={cn(
                                    'text-xs',
                                    f.level === 'error' ? 'text-destructive' : 'text-cyber-amber',
                                  )}
                                >
                                  {f.msg}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Connection facts: who is publishing, or when it last did. */}
                      {(isConnected && stream.client_ip) || (!isConnected && stream.last_login) ? (
                        <dl className="flex flex-wrap gap-x-6 gap-y-1 py-2.5 text-xs">
                          {isConnected && stream.client_ip ? (
                            <div className="flex items-baseline gap-1.5">
                              <dt className="text-muted-foreground">Publisher IP</dt>
                              <dd className="font-mono text-foreground">{stream.client_ip}</dd>
                            </div>
                          ) : null}
                          {!isConnected && stream.last_login ? (
                            <div className="flex items-baseline gap-1.5">
                              <dt className="text-muted-foreground">Last seen</dt>
                              <dd className="font-mono tabular-nums text-foreground" title={formatDateTime(stream.last_login, { withZone: true })}>
                                {formatDateTime(stream.last_login)}
                              </dd>
                            </div>
                          ) : null}
                        </dl>
                      ) : null}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </SectionCard>

      {/* Armed flows at a glance so operators don't need the Flows tab just to check state. */}
      <SectionCard
        title="Armed flows"
        description="Flows with keys allocated. On air means the selected output is receiving a signal; armed means it is waiting for one."
      >
        <LiveFlowsPanel flows={activeFlows} loading={flowsQuery.isLoading} />
      </SectionCard>

      <ConfirmDialog
        open={pendingLiveStream !== null}
        onOpenChange={(open) => {
          if (!open) setPendingLiveStream(null)
        }}
        title={pendingLiveStream ? `Make “${pendingLiveStream.name}” the active key?` : ''}
        description="The public player switches to this key on its next poll. Any recording pinned to the front page is cleared."
        confirmLabel="Set active"
        busyLabel="Setting…"
        busy={setLiveMutation.isPending}
        error={pendingLiveStream ? setLiveError : null}
        onConfirm={() => {
          if (pendingLiveStream) setLiveMutation.mutate(pendingLiveStream.user_id)
        }}
        data-testid="admin-streams-set-live-confirm"
      />

      {/* HLS preview modal — purely client-side video viewer. */}
      <Dialog
        open={previewStream !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewToken(null)
        }}
      >
        <DialogContent data-testid="admin-streams-preview" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {previewStream ? `Preview · ${previewStream.name}` : 'Preview'}
            </DialogTitle>
            <DialogDescription>
              Live HLS playback for this stream key. Browsers without native
              HLS support may show an error until an HLS shim is loaded.
            </DialogDescription>
          </DialogHeader>
          {previewStream?.stream_url ? (
            <video
              key={previewStream.token}
              controls
              autoPlay
              muted
              playsInline
              className="w-full rounded-md border border-border/40 bg-black"
              src={previewStream.stream_url}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              This key is not producing an HLS playlist right now.
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPreviewToken(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed snippet generator — pure client-side composition. */}
      <SnippetDialog
        open={snippetToken !== null}
        onOpenChange={(open) => {
          if (!open) setSnippetToken(null)
        }}
        selectedToken={snippetToken}
        onSelectToken={setSnippetToken}
        streams={streams}
        embedOrigins={embedOriginsQuery.data?.origins ?? []}
      />

      {/* Add key — collect a name; the backend mints the key. */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (createKeyMutation.isPending) return
          setCreateOpen(open)
          if (!open) setCreateError(null)
        }}
      >
        <DialogContent data-testid="admin-streams-add">
          <DialogHeader>
            <DialogTitle>Add stream key</DialogTitle>
            <DialogDescription>
              The key itself is generated for you. Give it a name so you can
              tell your keys apart, for example “Main camera” or “Backup”.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              const trimmed = createLabel.trim()
              if (trimmed === '' || createKeyMutation.isPending) return
              createKeyMutation.mutate(trimmed)
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="admin-streams-add-label">Name</Label>
              <Input
                id="admin-streams-add-label"
                data-testid="admin-streams-add-label"
                autoFocus
                value={createLabel}
                onChange={(e) => setCreateLabel(e.target.value)}
                placeholder="Main camera"
              />
            </div>
            {createError !== null && (
              <InlineError message={createError} data-testid="admin-streams-add-error" />
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={createKeyMutation.isPending}
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="admin-streams-add-submit"
                disabled={createKeyMutation.isPending || createLabel.trim() === ''}
              >
                {createKeyMutation.isPending ? 'Creating…' : 'Create key'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
        title={pendingDelete ? `Delete “${pendingDelete.name}”?` : 'Delete stream key?'}
        description={
          pendingDelete?.rtmp_connected
            ? 'An encoder is publishing to this key right now; it is disconnected immediately and viewers lose the stream. The key and its viewing portal address are removed permanently. This can’t be undone.'
            : 'Any encoder still configured with this key can no longer publish. The key and its viewing portal address are removed permanently. This can’t be undone.'
        }
        confirmLabel="Delete key"
        busyLabel="Deleting…"
        confirmVariant="destructive-solid"
        busy={deleteKeyMutation.isPending}
        error={pendingDelete ? deleteError : null}
        returnFocusTo={menuTriggerRef}
        onConfirm={() => {
          if (pendingDelete) deleteKeyMutation.mutate(pendingDelete.user_id)
        }}
        data-testid="admin-streams-delete-confirm"
      />

      <ConfirmDialog
        open={pendingRegenerate !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRegenerate(null)
        }}
        title={pendingRegenerate ? `Regenerate the key for “${pendingRegenerate.name}”?` : 'Regenerate stream key?'}
        description={
          pendingRegenerate?.rtmp_connected
            ? 'The current key stops working immediately and the encoder publishing with it is disconnected, so the stream is interrupted until it reconnects with the new key. The new key stays on screen until you hide it.'
            : 'The current key stops working immediately. Update every encoder that uses it. The new key stays on screen until you hide it.'
        }
        confirmLabel="Regenerate key"
        busyLabel="Regenerating…"
        confirmVariant="destructive-solid"
        busy={regenerateTokenMutation.isPending}
        error={pendingRegenerate ? regenerateError : null}
        returnFocusTo={menuTriggerRef}
        onConfirm={() => {
          if (!pendingRegenerate) return
          setRegeneratedTokens((prev) => {
            const next = { ...prev }
            delete next[pendingRegenerate.id]
            return next
          })
          regenerateTokenMutation.mutate({
            streamId: pendingRegenerate.id,
            userId: pendingRegenerate.user_id,
          })
        }}
        data-testid="admin-streams-regenerate-confirm"
      />
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────

/** The one state mark for a key: Live (green, pulsing) only while a signal arrives. */
function KeyStatePill({ live }: { live: boolean }) {
  return (
    <span
      role="status"
      title={live ? 'An encoder is publishing to this key.' : 'No encoder is publishing to this key.'}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        live
          ? 'border-cyber-green/40 bg-cyber-green/15 text-cyber-green'
          : 'border-border/60 bg-muted/40 text-muted-foreground',
      )}
    >
      <span className="relative flex h-2 w-2 shrink-0 items-center justify-center" aria-hidden="true">
        {live ? (
          <span className="animate-live-dot absolute inline-flex h-full w-full rounded-full bg-cyber-green opacity-75" />
        ) : null}
        <span className={cn('relative h-1.5 w-1.5 rounded-full', live ? 'bg-cyber-green' : 'bg-muted-foreground/70')} />
      </span>
      {live ? 'Live' : 'Idle'}
    </span>
  )
}

function BitrateStat({
  label,
  parts,
}: {
  label: string
  parts: { value: string; unit: string }
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 sm:flex-col sm:items-end sm:gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="whitespace-nowrap text-right text-sm font-mono tabular-nums text-foreground">
        {parts.value}
        {parts.unit ? <span className="ml-1 text-xs text-muted-foreground">{parts.unit}</span> : null}
      </dd>
    </div>
  )
}

function SnippetDialog({
  open,
  onOpenChange,
  selectedToken,
  onSelectToken,
  streams,
  embedOrigins,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedToken: string | null
  onSelectToken: (token: string | null) => void
  streams: StreamSummary[]
  embedOrigins: string[]
}) {
  const [width, setWidth] = useState<string>('640')
  const [responsive, setResponsive] = useState<boolean>(false)
  const [selectedOrigin, setSelectedOrigin] = useState<string>('')
  const [copyState, setCopyState] = useState<'idle' | 'success' | 'error'>('idle')

  // Default the origin to either the first configured embed origin or
  // the current window origin so the snippet is paste-able by default.
  useEffect(() => {
    if (!open) return
    if (selectedOrigin !== '') return
    const fallback =
      embedOrigins[0] ??
      (typeof window !== 'undefined' ? window.location.origin : '')
    setSelectedOrigin(fallback)
  }, [open, embedOrigins, selectedOrigin])

  useEffect(() => {
    if (!open) {
      setCopyState('idle')
      setWidth('640')
      setResponsive(false)
    }
  }, [open])

  const active = useMemo<StreamSummary | null>(() => {
    if (!streams.length) return null
    if (selectedToken) {
      const match = streams.find((s) => s.token === selectedToken)
      if (match) return match
    }
    return streams[0] ?? null
  }, [streams, selectedToken])

  const normalizedOrigin = selectedOrigin.replace(/\/$/, '')
  const sanitizedWidth = (() => {
    const numeric = width.replace(/[^0-9]/g, '')
    if (!numeric) return '640'
    const parsed = parseInt(numeric, 10)
    if (!parsed || parsed < 100) return '640'
    return String(parsed)
  })()

  const snippet: string = useMemo(() => {
    if (!active || !normalizedOrigin) return ''
    const attrs = [
      `data-livevane-player`,
      `data-token="${active.token}"`,
      responsive ? `data-responsive="true"` : '',
      responsive
        ? `style="max-width:${sanitizedWidth}px"`
        : `style="width:${sanitizedWidth}px"`,
    ].filter(Boolean)
    return [
      `<link rel="stylesheet" href="${normalizedOrigin}/embed/player.css">`,
      `<script src="${normalizedOrigin}/embed/player.js"></script>`,
      `<div ${attrs.join(' ')}></div>`,
    ].join('\n')
  }, [active, normalizedOrigin, responsive, sanitizedWidth])

  const copySnippet = async (): Promise<void> => {
    if (!snippet) return
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(snippet)
        setCopyState('success')
      } else {
        setCopyState('error')
      }
    } catch {
      setCopyState('error')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-streams-snippet" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Embed snippet</DialogTitle>
          <DialogDescription>
            Paste this into the page where you want the player to appear.
            The origin defaults to the first allowlisted embed origin;
            change it if your page lives elsewhere.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="admin-streams-snippet-key">Stream key</Label>
            <Select
              value={active?.token ?? ''}
              onValueChange={(v) => onSelectToken(v || null)}
            >
              <SelectTrigger id="admin-streams-snippet-key">
                <SelectValue placeholder="Pick a stream key" />
              </SelectTrigger>
              <SelectContent>
                {streams.map((s) => (
                  <SelectItem key={s.token} value={s.token}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="admin-streams-snippet-width">Width (px)</Label>
              <Input
                id="admin-streams-snippet-width"
                type="text"
                inputMode="numeric"
                value={width}
                onChange={(e) => setWidth(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-cyber-cyan"
                  checked={responsive}
                  onChange={(e) => setResponsive(e.target.checked)}
                />
                Responsive (max-width)
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-streams-snippet-origin">Embed origin</Label>
            <Input
              id="admin-streams-snippet-origin"
              type="text"
              value={selectedOrigin}
              onChange={(e) => setSelectedOrigin(e.target.value)}
              placeholder="https://example.com"
            />
            {embedOrigins.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Allowlist: <span className="font-mono">{embedOrigins.join(', ')}</span>
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-streams-snippet-code">Snippet</Label>
            <Textarea
              id="admin-streams-snippet-code"
              readOnly
              value={snippet}
              className="font-mono text-xs"
              rows={6}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button type="button" disabled={!snippet} onClick={() => void copySnippet()}>
            {copyState === 'success'
              ? 'Copied'
              : copyState === 'error'
                ? 'Copy failed'
                : 'Copy snippet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Armed flows panel ───────────────────────────────────────────────

/**
 * Compact read-only list of ACTIVE===1 flows. Flow-builder controls are
 * intentionally absent — use the Flows page for edits.
 */
function LiveFlowsPanel({
  flows,
  loading,
}: {
  flows: FlowListItem[]
  loading: boolean
}) {
  return (
    <div className="space-y-3" data-testid="admin-streams-live-flows">
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-4/5" />
          <Skeleton className="h-8 w-3/5" />
        </div>
      ) : flows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 px-4 py-5 text-center">
          <p
            data-testid="admin-streams-flows-empty"
            className="text-sm text-muted-foreground"
          >
            No flows are armed. Arm one on the Flows page to allocate its keys.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border/40">
          {flows.map((flow) => {
            const state = getFlowState(flow)
            return (
              <li key={flow.ID} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground" title={flow.NAME}>
                  {flow.NAME}
                </span>
                {flow.LIVE?.connected ? (
                  <span className="hidden whitespace-nowrap font-mono text-xs tabular-nums text-muted-foreground sm:inline">
                    {formatBitrate(flow.LIVE.bitrate_kbps)}
                  </span>
                ) : null}
                <FlowStatePill state={state} />
              </li>
            )
          })}
        </ul>
      )}
      <div className="flex justify-end pt-1">
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to="/flows">Open flows</Link>
        </Button>
      </div>
    </div>
  )
}
